import React, { useState, useEffect } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import CircularProgress from "@mui/material/CircularProgress";
import Autocomplete from "@mui/material/Autocomplete";
import InputAdornment from "@mui/material/InputAdornment";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import DriveFileRenameOutlineRoundedIcon from "@mui/icons-material/DriveFileRenameOutlineRounded";
import LinkRoundedIcon from "@mui/icons-material/LinkRounded";
import FolderRoundedIcon from "@mui/icons-material/FolderRounded";
import { doc, setDoc, deleteDoc, updateDoc, getDoc } from "firebase/firestore";
import { db } from "./firebaseConfig";

function Section({ icon, title, children }) {
  return (
    <Box sx={{ py: 2.5 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
        {icon}
        <Typography
          variant="caption"
          fontWeight={700}
          color="text.secondary"
          sx={{ textTransform: "uppercase", letterSpacing: 0.8 }}
        >
          {title}
        </Typography>
      </Box>
      {children}
    </Box>
  );
}

export default function ManageLinkDialog({ open, onClose, link, existingFolders, onUpdated, onNotify }) {
  const [slug, setSlug] = useState("");
  const [destUrl, setDestUrl] = useState("");
  const [folder, setFolder] = useState("");
  const [saving, setSaving] = useState({});
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (link) {
      setSlug("");
      setDestUrl(link.URL || "");
      setFolder(link.folder || "");
      setSaving({});
      setErrors({});
    }
  }, [link]);

  const startSave = (key) => { setSaving((s) => ({ ...s, [key]: true })); setErrors((e) => ({ ...e, [key]: "" })); };
  const stopSave  = (key) => setSaving((s) => ({ ...s, [key]: false }));
  const setErr    = (key, msg) => setErrors((e) => ({ ...e, [key]: msg }));

  /* ── Rename ───────────────────────────────────────────── */
  const handleRename = async () => {
    const trimmed = slug.trim();
    if (!trimmed) return;
    if (!/^[a-zA-Z0-9]+$/.test(trimmed)) { setErr("slug", "Letters and numbers only."); return; }
    startSave("slug");
    try {
      const existing = await getDoc(doc(db, "linksCustom", trimmed));
      if (existing.exists()) { setErr("slug", "This alias is already taken."); stopSave("slug"); return; }
      const oldCol = link.isCustom ? "linksCustom" : "links";
      await setDoc(doc(db, "linksCustom", trimmed), {
        URL: link.URL,
        uid: link.uid,
        clicks: link.clicks || 0,
        createdAt: link.createdAt || null,
        folder: link.folder || null,
      });
      await deleteDoc(doc(db, oldCol, link.id));
      onUpdated({ ...link, id: trimmed, isCustom: true });
      onNotify("Link renamed.");
      onClose();
    } catch { setErr("slug", "Something went wrong."); }
    stopSave("slug");
  };

  /* ── Change destination ───────────────────────────────── */
  const handleChangeUrl = async () => {
    const trimmed = destUrl.trim();
    if (!trimmed) return;
    const normalized = /^https?:\/\//i.test(trimmed) ? trimmed : "https://" + trimmed;
    startSave("url");
    try {
      await updateDoc(doc(db, link.isCustom ? "linksCustom" : "links", link.id), { URL: normalized });
      onUpdated({ ...link, URL: normalized });
      onNotify("Destination updated.");
      setDestUrl(normalized);
    } catch { setErr("url", "Something went wrong."); }
    stopSave("url");
  };

  /* ── Move to folder ───────────────────────────────────── */
  const handleMoveFolder = async () => {
    startSave("folder");
    try {
      const folderValue = folder.trim() || null;
      await updateDoc(doc(db, link.isCustom ? "linksCustom" : "links", link.id), { folder: folderValue });
      onUpdated({ ...link, folder: folderValue });
      onNotify(folderValue ? `Moved to "${folderValue}".` : "Removed from folder.");
    } catch { setErr("folder", "Something went wrong."); }
    stopSave("folder");
  };

  if (!link) return null;

  const shortUrl = `${window.location.hostname}/${link.id}`;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { borderRadius: 5, width: "100%", maxWidth: 460, m: 2 } }}
    >
      <DialogTitle sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", pb: 0 }}>
        <Box>
          <Typography fontWeight={800} fontSize="1.1rem">Manage Link</Typography>
          <Typography variant="body2" color="primary.main" fontWeight={600} noWrap sx={{ mt: 0.25 }}>
            {shortUrl}
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small" sx={{ mt: -0.5, ml: 1 }}>
          <CloseRoundedIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        {/* ── Rename ── */}
        <Section
          icon={<DriveFileRenameOutlineRoundedIcon sx={{ fontSize: 15, color: "text.secondary" }} />}
          title="Rename"
        >
          <TextField
            fullWidth
            size="small"
            label="New alias"
            placeholder="my-new-name"
            value={slug}
            onChange={(e) => { setSlug(e.target.value); setErr("slug", ""); }}
            error={!!errors.slug}
            helperText={errors.slug || "Warning: changing the alias breaks any existing shares."}
            slotProps={{
              input: {
                startAdornment: (
                  <InputAdornment position="start">
                    <Typography variant="body2" color="text.disabled" noWrap>
                      {window.location.hostname + "/"}
                    </Typography>
                  </InputAdornment>
                ),
              },
            }}
          />
          <Button
            variant="contained"
            size="small"
            onClick={handleRename}
            disabled={!slug.trim() || saving.slug}
            startIcon={saving.slug ? <CircularProgress size={14} color="inherit" /> : null}
            sx={{ mt: 1.5, borderRadius: 50 }}
          >
            {saving.slug ? "Saving…" : "Save rename"}
          </Button>
        </Section>

        <Divider />

        {/* ── Destination ── */}
        <Section
          icon={<LinkRoundedIcon sx={{ fontSize: 15, color: "text.secondary" }} />}
          title="Destination"
        >
          <TextField
            fullWidth
            size="small"
            label="Destination URL"
            value={destUrl}
            onChange={(e) => { setDestUrl(e.target.value); setErr("url", ""); }}
            error={!!errors.url}
            helperText={errors.url || "The short link stays the same — only the target changes."}
          />
          <Button
            variant="contained"
            size="small"
            onClick={handleChangeUrl}
            disabled={!destUrl.trim() || saving.url}
            startIcon={saving.url ? <CircularProgress size={14} color="inherit" /> : null}
            sx={{ mt: 1.5, borderRadius: 50 }}
          >
            {saving.url ? "Saving…" : "Save destination"}
          </Button>
        </Section>

        <Divider />

        {/* ── Folder ── */}
        <Section
          icon={<FolderRoundedIcon sx={{ fontSize: 15, color: "text.secondary" }} />}
          title="Folder"
        >
          <Autocomplete
            freeSolo
            options={existingFolders}
            value={folder}
            onInputChange={(_, val) => { setFolder(val); setErr("folder", ""); }}
            renderInput={(params) => (
              <TextField
                {...params}
                size="small"
                label="Folder name"
                placeholder="e.g. Marketing, Personal…"
                error={!!errors.folder}
                helperText={errors.folder || "Leave empty to remove from any folder."}
              />
            )}
          />
          <Button
            variant="contained"
            size="small"
            onClick={handleMoveFolder}
            disabled={saving.folder}
            startIcon={saving.folder ? <CircularProgress size={14} color="inherit" /> : null}
            sx={{ mt: 1.5, borderRadius: 50 }}
          >
            {saving.folder ? "Saving…" : "Save folder"}
          </Button>
        </Section>
      </DialogContent>
    </Dialog>
  );
}
